import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, merge } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CustomerService, CustomerDTO } from '../../services/customer.service';
import { SaleService, type SaleDTO } from '../../services/sale.service';
import { CustomerReadComponent } from './customer-read.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import { SubscriptionReadComponent } from '../subscriptions/subscription-read.component';
import { SubscriptionService } from '../../services/subscription.service';
import type { SubscriptionDTO } from '../../core/models';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import {
  CompanyTypeEnum,
  CompanyTypeOptions,
  CustomerFilterSpec,
  EntityState,
  getCompanyTypeLabel,
  type ChargeDTO
} from '../../core/models';

@Component({
  selector: 'app-customers-list',
  templateUrl: './customers-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .customers-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        color: rgb(100 116 139); /* slate-500 */
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        transition: background-color 0.15s ease, color 0.15s ease;
      }
      :host ::ng-deep .customers-primeng-table .p-datatable-thead > tr > th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .customers-primeng-table .p-datatable-thead > tr > th.p-sortable-column:hover {
        background: rgb(241 245 249); /* slate-100 */
        color: rgb(15 23 42); /* slate-900 */
      }
      :host ::ng-deep .customers-primeng-table .p-datatable-thead > tr > th.p-sortable-column:focus-visible {
        outline: 2px solid rgb(14 165 233);
        outline-offset: -2px;
      }
      :host ::ng-deep .customers-primeng-table .p-datatable-thead > tr > th.p-sortable-column.p-datatable-column-sorted {
        background: rgb(240 249 255); /* sky-50 */
        color: rgb(3 105 161); /* sky-700 */
      }
      :host ::ng-deep .customers-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-datatable-sort-icon,
      :host ::ng-deep .customers-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-sortable-column-icon,
      :host ::ng-deep .customers-primeng-table .p-datatable-thead > tr > th.p-sortable-column p-sorticon svg {
        width: 0.75rem;
        height: 0.75rem;
        color: rgb(14 165 233); /* sky-500 */
        opacity: 1;
        flex-shrink: 0;
        margin-left: auto;
      }
      :host ::ng-deep .customers-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .customers-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .customers-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important; /* sky-100 */
        border-color: rgb(14 165 233) !important; /* sky-500 */
        color: rgb(2 132 199) !important; /* sky-600 */
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-sale-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-sale-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-subscription-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-subscription-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    RouterLink,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    FloatLabelModule,
    SelectModule,
    DrawerModule,
    CustomerReadComponent,
    SaleReadComponent,
    SubscriptionReadComponent,
    MatTooltipModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    ButtonModule,
    SkeletonModule
  ]
})
export class CustomersListComponent {
  private customerService = inject(CustomerService);
  private saleService = inject(SaleService);
  private subscriptionService = inject(SubscriptionService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  selectedCustomer = signal<CustomerDTO | null>(null);
  isDetailsOpen = signal(false);
  isSaleDetailsOpen = signal(false);
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  saleDetailsLoading = signal(false);

  selectedSubscription = signal<SubscriptionDTO | null>(null);
  isSubscriptionDetailsOpen = signal(false);
  subscriptionDetailsLoading = signal(false);

  deleteModalOpen = signal(false);
  deleteModalShown = signal(false);
  customerToDelete = signal<CustomerDTO | null>(null);
  deleteLoading = signal(false);

  customers = signal<CustomerDTO[]>([]);
  loading = signal(false);
  /** True después de la primera carga (éxito o error). Distingue primera carga (skeleton) de actualizaciones (solo loading de PrimeNG). */
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  detailsLoading = signal(false);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];
  /** Campo de ordenamiento activo (una sola columna a la vez). */
  sortField = signal('legalName');
  /** 1 = asc, -1 = desc */
  sortOrder = signal(1);

  /** Filas para el skeleton de la primera carga (solo referencia para @for) */
  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  private readonly allFilterValue = 'ALL';

  filtersForm = this.fb.group({
    q: [''],
    type: [this.allFilterValue],
    state: [EntityState.ACTIVE]
  });

  hasActiveFilters = signal(false);

  readonly CompanyTypeEnum = CompanyTypeEnum;
  readonly getCompanyTypeLabel = getCompanyTypeLabel;

  typeFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    ...CompanyTypeOptions
  ];

  stateFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    { value: EntityState.ACTIVE, label: 'Activo' },
    { value: EntityState.INACTIVE, label: 'Inactivo' }
  ];

  constructor() {
    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    merge(
      this.filtersForm.controls.type.valueChanges,
      this.filtersForm.controls.state.valueChanges
    ).pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());

    this.loadCustomers();
  }

  loadCustomers(): void {
    this.loading.set(true);
    this.error.set(null);

    const spec = this.buildSpecFromFilters();
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    const pageable = {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    };

    this.customerService.getCustomers(spec, pageable).subscribe({
      next: (response) => {
        if (response.ok) {
          this.customers.set(response.body);
          if (response.page) {
            this.totalItems.set(response.page.totalItems);
            this.totalPages.set(response.page.totalPages);
            this.pageSize.set(response.page.pageSize);
            this.currentPage.set(Math.max(0, response.page.currentPage - 1));
          } else {
            this.totalItems.set(response.body.length);
            this.totalPages.set(this.getTotalPages());
          }
        } else {
          this.error.set(response.message || 'Error al cargar clientes');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        this.error.set('Error al conectar con el servidor. Verifica que el API esté ejecutándose.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
        console.error('Error loading customers:', err);
      }
    });
  }

  onPageChange(page: number): void {
    const total = this.totalPages();
    if (page < 0) return;
    if (total > 0 && page >= total) return;
    this.currentPage.set(page);
    this.loadCustomers();
  }

  onPageSizeChange(size: number): void {
    if (!size || size === this.pageSize()) return;
    this.pageSize.set(size);
    this.currentPage.set(0);
    this.loadCustomers();
  }

  /** Lazy load para la tabla PrimeNG: paginación y ordenamiento en servidor */
  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows || this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged =
      nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();

    // Al cambiar el ordenamiento siempre volver a la página 1
    const page = sortChanged
      ? 0
      : event.rows
        ? Math.floor((event.first ?? 0) / event.rows)
        : 0;

    if (
      !sortChanged &&
      page === this.currentPage() &&
      size === this.pageSize()
    ) {
      return;
    }

    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadCustomers();
  }

  openCustomerDetails(customer: CustomerDTO): void {
    if (!customer?.id) return;
    this.isDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.detailsLoading.set(true);
    this.customerService.getCustomerById(customer.id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.selectedCustomer.set(response.body);
        } else {
          this.error.set(response.message || 'Error al cargar el cliente');
        }
        this.detailsLoading.set(false);
      },
      error: (err) => {
        this.error.set('Error al cargar el cliente. Verifica que el API esté ejecutándose.');
        this.detailsLoading.set(false);
        console.error('Error loading customer by id:', err);
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  editFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  openSaleDetails(saleId: number): void {
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: (chargesResp) => {
              this.saleCharges.set(chargesResp.body ?? []);
            },
            error: () => this.saleCharges.set([])
          });
        } else {
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: (err) => {
        this.saleDetailsLoading.set(false);
        this.closeSaleDetails();
        console.error('Error loading sale detail:', err);
      }
    });
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  editSaleFromModal(): void {
    const sale = this.selectedSale();
    if (!sale?.id) return;
    this.closeSaleDetails();
    this.router.navigate(['/sales', sale.id]);
  }

  openCustomerFromSale(customerId: number): void {
    if (!customerId) return;
    this.closeSaleDetails();
    const current = this.selectedCustomer();
    if (current?.id === customerId) {
      this.isDetailsOpen.set(true);
      return;
    }
    this.isDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.detailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.error.set(response.message || 'Error al cargar el cliente');
        }
        this.detailsLoading.set(false);
      },
      error: (err) => {
        this.error.set('Error al cargar el cliente. Verifica que el API esté ejecutándose.');
        this.detailsLoading.set(false);
        console.error('Error loading customer by id:', err);
      }
    });
  }

  openSubscriptionFromSale(subscriptionId: number): void {
    if (!subscriptionId) return;
    this.isSubscriptionDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(subscriptionId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.error.set(response.message || 'Error al cargar la suscripción');
          this.closeSubscriptionDetails();
        }
        this.subscriptionDetailsLoading.set(false);
      },
      error: (err) => {
        this.error.set('Error al cargar la suscripción');
        this.subscriptionDetailsLoading.set(false);
        this.closeSubscriptionDetails();
        console.error('Error loading subscription by id:', err);
      }
    });
  }

  closeSubscriptionDetails(): void {
    this.isSubscriptionDetailsOpen.set(false);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(false);
  }

  onSubscriptionDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSubscriptionDetails();
  }

  editSubscriptionFromModal(): void {
    const subscription = this.selectedSubscription();
    if (!subscription?.id) return;
    this.closeSubscriptionDetails();
    this.router.navigate(['/subscriptions', subscription.id]);
  }

  editCustomer(customer: CustomerDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!customer?.id) return;
    this.router.navigate(['/customers', customer.id]);
  }

  openDeleteModal(customer: CustomerDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!customer?.id) return;
    this.customerToDelete.set(customer);
    this.deleteModalOpen.set(true);
    setTimeout(() => this.deleteModalShown.set(true), 10);
  }

  closeDeleteModal(): void {
    this.deleteModalShown.set(false);
    setTimeout(() => {
      this.deleteModalOpen.set(false);
      this.customerToDelete.set(null);
    }, 200);
  }

  confirmDeleteCustomer(): void {
    const customer = this.customerToDelete();
    if (!customer?.id) return;

    this.deleteLoading.set(true);
    this.customerService.deleteCustomer(customer.id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.loadCustomers();
          this.closeDeleteModal();
        } else {
          this.error.set(response.message || 'Error al eliminar el cliente');
        }
        this.deleteLoading.set(false);
      },
      error: (err) => {
        this.error.set(err.error?.message || 'Error al eliminar el cliente.');
        this.deleteLoading.set(false);
      }
    });
  }

  applyFilters(): void {
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.loadCustomers();
  }

  clearFilters(): void {
    this.filtersForm.reset(
      { q: '', type: this.allFilterValue, state: EntityState.ACTIVE },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(raw.q?.trim() || this.isSpecificFilter(raw.type) || raw.state !== EntityState.ACTIVE)
    );
  }

  private buildSpecFromFilters(): CustomerFilterSpec {
    const value = this.filtersForm.getRawValue();
    const spec: CustomerFilterSpec = {};
    const q = value.q?.trim();

    if (q) {
      spec['q[lk]'] = q;
    }
    if (this.isSpecificFilter(value.type)) {
      spec.type = value.type;
    }
    if (this.isSpecificFilter(value.state)) {
      spec.state = value.state;
    } else {
      spec.state = [EntityState.ACTIVE, EntityState.INACTIVE];
    }

    return spec;
  }

  private isSpecificFilter(value: string | null | undefined): boolean {
    return !!value && value !== this.allFilterValue;
  }

  getTotalPages(): number {
    const total = this.totalPages();
    if (total > 0) return total;
    return Math.ceil(this.totalItems() / this.pageSize());
  }

  getDisplayName(customer: CustomerDTO): string {
    if (customer.type === CompanyTypeEnum.PERSON) {
      return `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.legalName;
    }
    return customer.comercialName || customer.legalName;
  }

  getPrimaryEmail(customer: CustomerDTO): string {
    if (customer.email) return customer.email;
    const primaryContact = customer.contacts?.find(c => c.type === 'PRIMARY' || c.type === 'EMAIL');
    return primaryContact?.email || '-';
  }

  getPrimaryPhone(customer: CustomerDTO): string {
    if (customer.phone) return customer.phone;
    const primaryContact = customer.contacts?.find(c => c.type === 'PRIMARY' || c.type === 'MOBILE' || c.type === 'OFFICE');
    return primaryContact?.phone || '-';
  }

  getStateSeverity(state: string): 'success' | 'danger' | null {
    if (state === 'ACTIVE') return 'success';
    if (state === 'INACTIVE') return 'danger';
    return null;
  }
}
